import { type ReactElement, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { refreshSession, useAuthStore, useIdentityAvailabilityQuery } from '@/entities/user';
import { useVerifyEmail } from '@/features/auth';
import { getErrorMessage } from '@/shared/lib';
import { buttonVariants, cn } from '@/shared/ui';
import { AuthShell } from '@/widgets/auth-shell';

type VerifyStatus =
  | { readonly step: 'pending' }
  /** signedIn — 이 브라우저에 로그인돼 있고 새 상태의 세션을 받았는지. 다음 단계 버튼의 목적지를 가른다. */
  | { readonly step: 'success'; readonly signedIn: boolean }
  | { readonly step: 'error'; readonly message: string };

const CONTINUE_BUTTON_CLASS =
  'mt-6 h-14 w-full rounded-none bg-primary text-[15px] font-semibold text-white hover:bg-primary/90 dark:text-primary-foreground';

/**
 * 메일 링크(/verify-email?token=...) 도착 페이지. 비로그인 상태에서도 열린다 —
 * 다른 기기 · 브라우저에서 링크를 열 수 있기 때문이다.
 */
export const VerifyEmailPage = (): ReactElement => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const { mutateAsync } = useVerifyEmail();
  // 본인인증 단계는 서버가 쓴다고 확인됐을 때만 안내한다(불러오는 중·실패면 이어서 이용하라고만).
  const identityRequired = useIdentityAvailabilityQuery().data?.identityRequired === true;
  const [status, setStatus] = useState<VerifyStatus>({ step: 'pending' });
  // StrictMode 의 이중 마운트에서 같은 토큰이 두 번 소비되지 않게 토큰별로 1회만
  // 실행한다 — 다른 토큰으로 재진입하면(링크 교체) 새로 검증한다.
  const firedToken = useRef<string | null>(null);

  useEffect(() => {
    if (!token || firedToken.current === token) {
      return;
    }

    firedToken.current = token;

    // 화면 전환은 뮤테이션 훅 상태(isSuccess 등)가 아니라 promise 로 구동한다 —
    // StrictMode 의 시뮬레이션 재마운트가 진행 중 뮤테이션에서 옵저버를 분리해
    // (TanStack v5 MutationObserver.onUnsubscribe) 훅 상태가 영영 갱신되지 않는다.
    mutateAsync({ token })
      .then(async () => {
        // 로그인 상태면 액세스 토큰의 status 가 옛값이므로 세션을 재발급받는다.
        // 재발급이 실패하면 낡은 status 클레임 토큰이 남아 보호 경로에서 403 이
        // 난다 — 세션을 비워 재로그인으로 유도한다. (인증 자체는 이미 성공)
        const wasSignedIn = useAuthStore.getState().isAuthenticated;
        const signedIn = wasSignedIn && (await refreshSession());
        if (wasSignedIn && !signedIn) {
          useAuthStore.getState().clear();
        }
        setStatus({ step: 'success', signedIn });
      })
      .catch((error: unknown) => {
        setStatus({ step: 'error', message: getErrorMessage(error, '인증에 실패했습니다.') });
      });
  }, [token, mutateAsync]);

  if (!token) {
    return (
      <AuthShell title="이메일 인증" description="인증 링크가 올바르지 않습니다.">
        <p className="text-sm text-muted-foreground">
          메일의 링크를 그대로 열었는지 확인해 주세요.
        </p>
      </AuthShell>
    );
  }

  if (status.step === 'error') {
    return (
      <AuthShell title="이메일 인증 실패" description="링크가 만료되었거나 유효하지 않습니다.">
        <p
          role="alert"
          className="border-l-2 border-destructive bg-destructive/5 px-4 py-3 text-sm text-destructive"
        >
          {status.message}
        </p>
        <p className="mt-4 text-sm text-muted-foreground">
          로그인 후 온보딩 화면에서 인증 메일을 다시 요청할 수 있습니다.
        </p>
        <Link to="/sign-in" className={cn(buttonVariants(), CONTINUE_BUTTON_CLASS)}>
          로그인으로 이동
        </Link>
      </AuthShell>
    );
  }

  if (status.step === 'success') {
    // 메일 링크는 가입하던 창과 다른 탭·기기에서 열리는 게 보통이다 — 주 안내는 "가입 화면으로 돌아가 [인증 완료]"다.
    // 이 브라우저에 로그인돼 있으면 [계속하기]로 온보딩에 바로 이어 주고(남은 단계는 온보딩이 상태를 보고 고른다),
    // 아니면 가입 화면을 닫은 경우를 위한 로그인 링크만 작게 둔다. 가입이 일반 · 소셜 어느 경로였든 같다.
    return (
      <AuthShell title="이메일 인증 완료" description="이메일 인증이 완료되었습니다.">
        <p className="border-l-2 border-brand-lime bg-muted/70 px-4 py-3 text-[15px] leading-relaxed">
          {identityRequired
            ? '가입 화면으로 돌아가 [인증 완료] 버튼을 눌러 주세요.'
            : '가입 화면으로 돌아가 [인증 완료] 버튼을 눌러 주세요. 바로 서비스를 이용하실 수 있습니다.'}
        </p>
        {status.signedIn ? (
          <Link to="/onboarding" className={cn(buttonVariants(), CONTINUE_BUTTON_CLASS)}>
            계속하기
          </Link>
        ) : (
          <p className="mt-4 text-sm text-muted-foreground">
            가입 화면을 닫으셨다면{' '}
            <Link
              to="/sign-in"
              className="font-semibold text-foreground underline underline-offset-4 hover:opacity-70"
            >
              로그인
            </Link>{' '}
            후 이어서 진행하실 수 있습니다.
          </p>
        )}
      </AuthShell>
    );
  }

  return (
    <AuthShell title="이메일 인증" description="인증을 처리하고 있습니다…">
      <p className="text-sm text-muted-foreground">잠시만 기다려 주세요.</p>
    </AuthShell>
  );
};
